import { formatDisplayDate } from '@/utils/date'
/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-05-08 15:17:24
 * @ Description: Table showing all of the organizations transactions
 */

import React, { useState } from 'react'
import Pagination from '@mui/material/Pagination'

// Centralized lodash imports - much cleaner!
import {
  map,
  find,
  ceil,
  slice,
  get,
  isString,
  every,
  isNil,
} from '@/utils/lodash'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  Menu,
  Table,
  TableCell,
  TableSortLabel,
  Tooltip,
  Box,
} from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import HousefoliosUser from '@/assets/images/Housefolios_user.png'
import { GET_TRANSACTIONS } from '../queries'
import withSession from '@/housefolios-components/Session/withSession'
import { enqueueSnackbar } from 'notistack'
import { GET_ME } from '@/housefolios-components/Session/queries'
import useSorting, {
  stableSort,
  getSorting,
} from '@/hooks/useSorting'
import { useQuery } from '@apollo/client/react'

import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'

// const status = [
//   {
//     name: orgCreator,
//     color: '#4290ff',
//   },
//   {
//     name: pending,
//     color: '#FF8300',
//   },
//   {
//     name: deactivated,
//     color: '#E4000F',
//   },
//   {
//     name: active,
//     color: '#46de64',
//   },
// ]

function TransactionsTableComponent(props) {
  const [{ order, orderBy }, { handleRequestSort }] = useSorting({
    defaultOrder: 'desc',
    defaultOrderBy: 'created',
  })

  const [page, setPage] = useState(
    parseInt(localStorage.getItem('team-page')) || 1,
  )
  const handleChange = (event, value) => {
    localStorage.setItem('team-page', value)
    setPage(value)
  }

  const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  }

  const tableHeaders = [
    { title: 'Payment Amount', name: 'amount' },
    { title: 'Refund Amount', name: 'amount_refunded' },
    { title: 'Description', name: 'description' },
    { title: 'Date', name: 'created' },
  ]

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const { loading, error, data } = useQuery(GET_TRANSACTIONS)

  if (loading) return <LoadingSkeleton variant="table" columnCount={4} />
  if (error) return `Error! ${error.message}`
  const count = ceil(data.transactions.length / 20)

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <Box sx={[{
        display: "flex",
        alignItems: "center",
        py: "0.75rem",
        px: "1.25rem",
        mb: 0,
        backgroundColor: "#fff",
        padding: "0.75rem 1.25rem",
        marginBottom: 0,
        borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
        "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
        "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
      }, { pr: '0.5rem' }]}>
        <Box sx={{
          flexGrow: 1,
          minWidth: 0,
          "& > b": { display: "block" },
          "& > small": {
            textTransform: "uppercase",
            display: "block",
            opacity: 0.3,
            fontWeight: "bold",
          },
        }}>
          <small>{props.session.me.activeOrg.name}</small>
          <b>Transactions</b>
        </Box>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",flexShrink:0}}></div>
      </Box>
      <CardContent sx={{ p: 0 }}>
        <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={[{ whiteSpace: 'nowrap', mb: 0 }, {
            width: "100%",
            marginBottom: "1rem",
            fontSize: "1rem",
            "& th, & td": { padding: "0.75rem", borderTop: "1px solid #e4e5f0", verticalAlign: "middle" },
            "& thead th": {
              textTransform: "uppercase",
              background: "#f4f5fd",
              fontSize: "0.875rem",
              verticalAlign: "middle",
              borderBottom: "2px solid #e4e5f0",
            },
            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
          }]}>
            <thead>
              <tr sx={{ textAlign: 'center' }}>
                {map(tableHeaders, (column) => (
                  <TableCell
                    key={column.title}
                    //style={{ whiteSpace: 'nowrap' }}
                    align={column.align}
                    sortDirection={
                      orderBy === column.name ? order : false
                    }
                  >
                    {column.name ? (
                      <TableSortLabel
                        active={orderBy === column.name}
                        direction={order}
                        onClick={(event) =>
                          handleRequestSort(event, column.name)
                        }
                      >
                        {column.title}
                        {orderBy === column.name ? (
                          <span style={visuallyHidden}>
                            {order === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.title
                    )}
                  </TableCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {map(
                slice(
                  stableSort(
                    data.transactions,
                    getSorting(order, orderBy),
                  ),
                  (page - 1) * 20,
                  page * 20,
                ),
                (transaction) => (
                  <tr>
                    <td>
                      <div>
                        {currencyFormat.format(
                          transaction.amount / 100,
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {transaction.amount_refunded
                          ? currencyFormat.format(
                              (-1 * transaction.amount_refunded) /
                                100,
                            )
                          : 0}
                      </div>
                    </td>
                    <td>
                      <div>{transaction.description}</div>
                    </td>
                    <td>
                      <div>
                        {formatDisplayDate(transaction.created)}
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </Table>
        </div>
        <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
        <Box sx={{ background: "#eeeff8", height: "1px", overflow: "hidden" }} />
        <Box
          sx={{
            p: '1rem',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination size="small" count={count} page={page} onChange={handleChange} />
        </Box>
      </CardContent>
    </Card>
  )
}

export default withSession(TransactionsTableComponent)
